import { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';

const features = [
  {
    title: 'Sinais sociais em tempo real',
    description: 'Monitoramento com IA para identificar movimentos emergentes no momento em que acontecem.'
  },
  {
    title: 'Leitura regional com profundidade',
    description: 'Contexto local via rede de agentes culturais combinada com agentes de IA.'
  },
  {
    title: 'Mapeamento de creators',
    description: 'Monitoramento ativo de criadores influentes em comunidades estratégicas.'
  },
  {
    title: 'Inteligencia Artificial preditiva',
    description: 'Modelos que antecipam padrões de comportamento e oportunidades.'
  },
  {
    title: 'Insights acionáveis diariamente',
    description: 'Alertas e relatórios prontos para virar ação.'
  },
  {
    title: 'Integração com times internos',
    description: 'Funciona com marketing, conteúdo, mídia ou inovação — sem necessidade de adaptação complexa. Os insights são prontos para uso imediato.'
  }
];

export default function WhatIsSection() {
  const [visibleItems, setVisibleItems] = useState<number[]>([]);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            features.forEach((_, index) => {
              setTimeout(() => {
                setVisibleItems((prev) => [...prev, index]);
              }, index * 150);
            });
          }
        });
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 bg-white">
      <div className="landing-container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-600">O que é o Culture Monitor?</h3>
            <h2 className="text-4xl font-bold leading-tight text-gray-900 font-['Times_New_Roman']">
              Uma plataforma de social intelligence com leitura cultural ágil e foco em performance.
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Combinamos tecnologia de monitoramento em tempo real com uma rede de agentes culturais espalhados por diferentes regiões do Brasil e da América Latina para revelar oportunidades que algoritmos sozinhos não enxergam.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
              {features.slice(0, 3).map((feature, index) => (
                <div
                  key={index}
                  className={`flex items-start space-x-3 transition-all duration-500 ${
                    visibleItems.includes(index) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  }`}
                >
                  <div className="relative w-6 h-6 flex-shrink-0 mt-1">
                    <Check
                      className={`w-6 h-6 text-black transition-all duration-700 ${
                        visibleItems.includes(index) ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
                      }`}
                      style={{
                        strokeDasharray: visibleItems.includes(index) ? '24' : '0',
                        strokeDashoffset: visibleItems.includes(index) ? '0' : '24',
                        transition: 'stroke-dashoffset 0.7s ease-in-out, opacity 0.3s ease-in-out, transform 0.3s ease-in-out'
                      }}
                    />
                  </div>
                  <div>
                    <h4 className="text-sm inline font-semibold bg-[#F2FF00] px-2 py-1 rounded text-black feature-title font-['Times_New_Roman']">
                      {feature.title}
                    </h4>
                    <p className="text-sm text-gray-600 mt-2">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="space-y-6">
              {features.slice(3, 6).map((feature, index) => (
                <div
                  key={index + 3}
                  className={`flex items-start space-x-3 transition-all duration-500 ${
                    visibleItems.includes(index + 3) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  }`}
                >
                  <div className="relative w-6 h-6 flex-shrink-0 mt-1">
                    <Check
                      className={`w-6 h-6 text-black transition-all duration-700 ${
                        visibleItems.includes(index + 3) ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
                      }`}
                      style={{
                        strokeDasharray: visibleItems.includes(index + 3) ? '24' : '0',
                        strokeDashoffset: visibleItems.includes(index + 3) ? '0' : '24',
                        transition: 'stroke-dashoffset 0.7s ease-in-out, opacity 0.3s ease-in-out, transform 0.3s ease-in-out'
                      }}
                    />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold bg-[#F2FF00] px-2 py-1 rounded text-black inline-block font-['Times_New_Roman']">
                      {feature.title}
                    </h4>
                    <p className="text-sm text-gray-600 mt-2">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

